import { useForm } from 'react-hook-form';
import { ApiCustomerIndividual, ApiUpdateCustomerLegalInfo } from '../../../openapi';
import {
  FormContentIsland,
  FormTextField,
  Grid,
  HookForm,
  Button,
  DateField,
  RadioGroupField,
} from '@razz-team/ui-web';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormError } from '../../../hooks/useFormError';
import { useUpdateCustomerLegalInfoMutation } from '../../../api/customers';
import { CountryField } from '../../Field/CountryField';

type FormValues = ApiUpdateCustomerLegalInfo;

export function UpdateLegalInfoForm({
  customer,
  submitText,
  onSuccess,
}: {
  customer: ApiCustomerIndividual;
  submitText?: string;
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  const mutation = useUpdateCustomerLegalInfoMutation({ onSuccess });

  const form = useForm<FormValues>({
    defaultValues: {
      legalName: {
        firstName: customer.name?.firstName ?? undefined,
        lastName: customer.name?.lastName ?? undefined,
        middleName: customer.name?.middleName ?? undefined,
      },
      birthInfo: {
        birthDate: customer.birthInfo?.birthDate ?? undefined,
        birthPlace: customer.birthInfo?.birthPlace ?? undefined,
      },
      legalSex: customer.legalSex ?? undefined,
      nationality: customer.nationality ?? undefined,
    },
  });

  useFormError(form, 'legalName', mutation.error);

  const handleSubmit = useCallback((values: FormValues) => mutation.mutate(values), [mutation]);

  return (
    <Grid
      flexDirection="column"
      gap={4}
      component={HookForm}
      form={form}
      onSubmit={handleSubmit}
      size={12}
    >
      <FormContentIsland>
        <FormTextField
          name="legalName.firstName"
          label={t('First name')}
          fullWidth
          required
          autoComplete="given-name"
          autoFocus
        />
        <FormTextField
          name="legalName.lastName"
          label={t('Last name')}
          fullWidth
          required
          autoComplete="family-name"
        />
        <FormTextField
          name="legalName.middleName"
          label={t('Middle name (optional)')}
          fullWidth
          autoComplete="additional-name"
        />
        <DateField name="birthInfo.birthDate" label={t('Date of Birth')} required />
        <CountryField name="birthInfo.birthPlace.country" label={t('Country of Birth')} required />
        <FormTextField
          name="birthInfo.birthPlace.city"
          label={t('City of Birth')}
          fullWidth
          autoComplete="off"
        />
        <RadioGroupField
          row
          name="legalSex"
          label={t('Legal sex')}
          options={[
            { label: t('Male'), value: 'M' },
            { label: t('Female'), value: 'F' },
          ]}
          required
        />
        <CountryField name="nationality" label={t('Nationality')} required />
      </FormContentIsland>
      <Button type="submit" size="large" loading={mutation.isPending} fullWidth>
        {submitText ?? t('Update')}
      </Button>
    </Grid>
  );
}
